// frontend/src/components/notifications/IOSInstallBanner.jsx
/**
 * IOSInstallBanner — nudges iOS users to add POG to home screen so they
 * can receive push notifications.
 *
 * @description
 *   iOS Safari does not deliver push notifications to standard browser
 *   tabs — only to PWAs installed on the home screen (Add to Home Screen).
 *   This banner detects iOS + non-standalone + not-recently-dismissed
 *   and shows a hint at the head of the content column with instructions.
 *
 *   🔴 **It sits IN THE PAGE FLOW, not floating.** Until W7 (2026-10-24) it
 *   was `position: fixed` at the bottom, and in iOS Safari it covered the
 *   composer of the new case view and the fixed table footers of the lists.
 *   A floating nudge can always cover *something*; a hint in the flow cannot.
 *   There is therefore no z-index here: it does not compete with any layer.
 *
 * @useWhen
 *   - Mounted once per app, inside the main content column
 *     (`SidebarAndHeaderBar.jsx`, above the routed page)
 *
 * @dontUseFor
 *   - Non-iOS browsers (renders nothing)
 *   - Already-installed PWA (renders nothing — checks navigator.standalone)
 *   - Dismissed within last 30 days
 *   - `vollflaeche` surfaces (the new case view): their column has a fixed
 *     height, an in-flow hint would shift it; pass `ausgeblendet`
 *
 * @related
 *   - usePushSubscription — only useful after user installs
 *
 * @props
 *   ausgeblendet: true on `vollflaeche` surfaces; renders nothing
 */
import { useState, useEffect } from 'react'
import { Box, Button, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { FS, SP } from '../../theme/tokens'

const STORAGE_KEY = 'pog.ios_install_banner_dismissed_at'
const DISMISS_DURATION_MS = 30 * 24 * 60 * 60 * 1000  // 30 days

/**
 * Der Stil des Hinweises: bewusst als Export, damit die Probe ihn pruefen
 * kann: `position`, `zIndex`, `bottom`, `left` und `right` muessen fehlen
 * (`IOSInstallBanner.test.jsx`, Fall a). Der Innenabstand kommt aus `SP`,
 * der seitliche Abstand nur mobil; auf dem Rechner traegt die
 * Inhaltsspalte selbst `px` (`desktopMainSx`).
 */
export const IOS_INSTALL_BANNER_SX = {
  mx: { xs: SP.sm, sm: 0 },
  mb: SP.sm,
  bgcolor: 'background.paper',
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: 4,
  p: SP.sm,
  display: 'flex',
  alignItems: 'flex-start',
  gap: SP.sm,
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent || '')
}

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true
}

function isRecentlyDismissed() {
  const at = localStorage.getItem(STORAGE_KEY)
  if (!at) return false
  const elapsed = Date.now() - parseInt(at, 10)
  return elapsed < DISMISS_DURATION_MS
}

/**
 * Soll der Hinweis stehen? Reine Ableitung, kein Zustand.
 *
 * 🔴 Sie laeuft im ANFANGSZUSTAND des `useState` (R16-C1, 2026-10-24), also
 * waehrend des ersten Renderns. Mit `useState(false)` plus `useEffect` erschien
 * das Banner erst NACH dem ersten Bild und schob den Inhalt um seine Hoehe
 * nach unten: ein Sprung von rund 90 px bei jedem Aufruf, und beim Schliessen
 * derselbe zurueck.
 */
function sollZeigen(ausgeblendet) {
  if (ausgeblendet) return false
  return isIOS() && !isStandalone() && !isRecentlyDismissed()
}

export default function IOSInstallBanner({ ausgeblendet = false }) {
  const [visible, setVisible] = useState(() => sollZeigen(ausgeblendet))

  // Nur fuer den Wechsel der Seite (`vollflaeche` an/aus). Beim ersten Rendern
  // setzt dieser Effekt denselben Wert, den der Anfangszustand schon traegt -
  // React verwirft den Render dann, es gibt keinen zweiten Paint.
  useEffect(() => {
    setVisible(sollZeigen(ausgeblendet))
  }, [ausgeblendet])

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()))
    setVisible(false)
  }

  if (ausgeblendet || !visible) return null

  return (
    <Box
      data-testid="ios-install-banner"
      role="region"
      aria-label="Install app"
      sx={IOS_INSTALL_BANNER_SX}
    >
      <Box sx={{ flexGrow: 1 }}>
        <Typography sx={{ fontSize: FS.sm, fontWeight: 600 }}>
          Add POG to your home screen
        </Typography>
        <Typography sx={{ fontSize: FS.xs, color: 'text.secondary', mt: 0.25 }}>
          Tap the share icon, then "Add to Home Screen". Push notifications work only after install.
        </Typography>
        <Button size="small" onClick={dismiss} sx={{ fontSize: FS.xs, mt: 0.5 }}>
          Don't show for 30 days
        </Button>
      </Box>
      <IconButton size="small" onClick={dismiss} aria-label="Dismiss">
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  )
}
